<div class="relative">
  <loading-overlay [isLoading]="loading()" />

  @if (result(); as r) {
    @if (r.items.length === 0 && !loading()) {
      <div class="empty-state">
        <i class="pi pi-inbox text-3xl mb-2 block"></i>
        {{ 'Cost_Analysis_No_Fabrics' | translateWF: 'No fabrics match the current filters.' }}
      </div>
    } @else {
      <div>
        <div class="fabric-list">
          @for (fabric of fabrics(); track fabric.id) {
            <article class="fabric-card" [class.is-expanded]="isExpanded(fabric.id)">
              <button type="button" class="fabric-card__header" (click)="toggleFabric(fabric)" [attr.aria-expanded]="isExpanded(fabric.id)">
                <i class="fabric-card__chevron pi" [class.pi-chevron-down]="isExpanded(fabric.id)" [class.pi-chevron-right]="!isExpanded(fabric.id)"></i>
                <div class="fabric-card__title">
                  @if (hasDescription(fabric)) {
                    <h3 class="fabric-card__desc">{{ fabric.subLabel }}</h3>
                  } @else {
                    <h3 class="fabric-card__desc">{{ fabric.label }}</h3>
                  }
                  <div class="row-sm-start-center">
                    @if (hasDescription(fabric)) {
                      <span class="code-badge">{{ fabric.label }}</span>
                    }
                    <app-cost-mode-chip [mode]="fabric.costMode" />
                    @if (standardSharePct(fabric) > 0) {
                      <span class="std-chip" [pTooltip]="'Cost_Analysis_Std_Chip_Tooltip' | translateWF: 'Lbs costed with Standard rates over this fabric.'" tooltipPosition="top">
                        <i class="pi pi-bolt std-chip__icon"></i>
                        <span class="tabular-nums">{{ standardSharePct(fabric) | number: '1.0-0' }}%</span>
                        <span class="std-chip__label">{{ 'Cost_Analysis_Std' | translateWF: 'Standard' }}</span>
                      </span>
                    }
                    @if (fabricCustomerName(fabric); as customer) {
                      <span
                        class="inline-flex max-w-[18rem] items-center gap-1.5 border-l border-[var(--bf-border-soft)] pl-3 text-xs text-[var(--bf-text-secondary)]"
                        [pTooltip]="customer"
                        tooltipPosition="top"
                      >
                        <i class="pi pi-user text-[0.65rem]"></i>
                        <span class="truncate">{{ customer }}</span>
                      </span>
                    }
                  </div>
                </div>
                <div class="fabric-card__metrics">
                  <div class="metric">
                    <span class="metric__value tabular-nums">{{ fabric.totalLbs | number: '1.0-0' }}</span>
                    <span class="metric__label">{{ 'Cost_Analysis_Lbs' | translateWF: 'Lbs' }}</span>
                  </div>
                  <div class="metric metric--total">
                    <span class="metric__value tabular-nums">${{ totalCostPerLb(fabric) | number: '1.4-4' }}</span>
                    <span class="metric__label">{{ 'Cost_Analysis_Total_Cost_Per_Lb' | translateWF: 'Total $/lb' }}</span>
                  </div>
                </div>
              </button>

              @if (isExpanded(fabric.id) && isFabricLoading(fabric.id)) {
                <div class="children-grid__loading" style="display: flex; justify-content: center; padding: 1rem; color: var(--bf-text-muted)">
                  <i class="pi pi-spin pi-spinner mr-2"></i>
                  {{ 'Cost_Analysis_Loading_Children' | translateWF: 'Loading details…' }}
                </div>
              }
              @if (isExpanded(fabric.id) && !isFabricLoading(fabric.id) && fabricChildren(fabric).length > 0) {
                <div class="children-grid">
                  <div class="children-grid__header">
                    <div class="cell cell--ident">{{ 'Cost_Analysis_Hierarchy' | translateWF: 'Hierarchy' }}</div>
                    <div class="cell cell--num">{{ 'Cost_Analysis_Lbs' | translateWF: 'Lbs' }}</div>
                    @for (process of processes; track process) {
                      <div class="cell cell--num cell--proc" [class.cell--raw]="process === 'RawMaterial'">{{ processLabels[process] }}</div>
                    }
                    <div class="cell cell--num cell--total">{{ 'Cost_Analysis_Total_Cost_Per_Lb' | translateWF: 'Total $/lb' }}</div>
                  </div>

                  @for (entry of visibleDescendants(fabric); track entry.node.id) {
                    @let descendant = entry.node;
                    @let depth = entry.depth;
                    <div class="tree-row" [attr.data-level]="descendant.level" [style.--depth]="depth">
                      <div class="cell cell--ident">
                        <button
                          type="button"
                          class="tree-row__chevron"
                          [class.tree-row__chevron--leaf]="descendant.children.length === 0"
                          (click)="toggle(descendant.id)"
                          [disabled]="descendant.children.length === 0"
                          [attr.aria-expanded]="descendant.children.length > 0 ? isExpanded(descendant.id) : null"
                        >
                          @if (descendant.children.length > 0) {
                            <i class="pi" [class.pi-chevron-down]="isExpanded(descendant.id)" [class.pi-chevron-right]="!isExpanded(descendant.id)"></i>
                          }
                        </button>
                        <div class="tree-row__title">
                          @if (descendant.level === 'color') {
                            <div class="row-sm-start-center">
                              @if (colorChip(descendant); as color) {
                                @switch (color.kind) {
                                  @case ('colored') {
                                    <span class="color-chip">
                                      <span class="color-chip__dot" [style.background-color]="color.swatch.bg"></span>
                                      <span class="color-chip__name">{{ color.name }}</span>
                                      @if (color.labDipNumber) {
                                        <span class="color-chip__lab">· {{ color.labDipNumber }}</span>
                                      }
                                    </span>
                                  }
                                  @case ('uncolored') {
                                    <span class="color-chip color-chip--uncolored">
                                      <i class="pi pi-circle color-chip__icon"></i>
                                      {{ 'Cost_Analysis_Color_Uncolored' | translateWF: 'Uncolored / Pending' }}
                                    </span>
                                  }
                                  @case ('orphan') {
                                    <span
                                      class="color-chip color-chip--orphan"
                                      [pTooltip]="'Cost_Analysis_Color_Orphan_Tooltip' | translateWF: 'Color reference not found in lab-dip catalog.'"
                                      tooltipPosition="top"
                                    >
                                      <i class="pi pi-question-circle color-chip__icon"></i>
                                      {{ color.diagnostic }}
                                    </span>
                                  }
                                }
                              }
                              <app-cost-mode-chip [mode]="descendant.costMode" />
                            </div>
                          } @else {
                            <div class="row-sm-start-center">
                              @if (hasDescription(descendant)) {
                                <span class="tree-row__label" [pTooltip]="descendant.subLabel ?? ''" tooltipPosition="top">
                                  {{ descendant.subLabel }}
                                </span>
                                <span class="code-badge">{{ descendant.label }}</span>
                              } @else {
                                <span class="tree-row__label">{{ descendant.label }}</span>
                              }
                            </div>

                            <div class="mt-1.5 row-start-center gap-3">
                              @if (workOrderTypeLabel(descendant); as woType) {
                                <span
                                  class="inline-flex items-center rounded-md px-1.5 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide leading-none"
                                  [class]="workOrderTypeStyle(descendant)"
                                >
                                  {{ woType }}
                                </span>
                              }
                              @if (descendant.level === 'productionCard' && pcStatusLabel(descendant.context.productionCardStatus); as pcStatus) {
                                <span
                                  class="inline-flex items-center rounded-md bg-slate-100 px-1.5 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide leading-none text-slate-700"
                                >
                                  {{ pcStatus }}
                                </span>
                              }
                              <app-cost-mode-chip [mode]="descendant.costMode" />
                            </div>
                          }
                        </div>
                      </div>
                      <div class="cell cell--num tabular-nums">{{ descendant.totalLbs | number: '1.0-0' }}</div>
                      @for (process of processes; track process) {
                        @let dbucket = bucketFor(descendant, process);
                        @let dIsRaw = process === 'RawMaterial';
                        <div class="cell cell--num cell--proc tabular-nums" [class.cell--raw]="dIsRaw">
                          @if (processCostMode(descendant, process); as pmode) {
                            <app-cost-mode-chip class="mr-1" [mode]="pmode" />
                          }
                          @if (dbucket && dbucket.totalCostUsd > 0 && !dIsRaw) {
                            <button
                              type="button"
                              class="proc-value inline-flex items-center gap-1 cursor-pointer"
                              (click)="onProcessClick(descendant, process)"
                              [attr.aria-label]="processLabels[process] + ' detail'"
                            >
                              <span>${{ dbucket.costPerLbUsd | number: '1.4-4' }}</span>
                              <i class="pi pi-arrow-circle-right text-[0.65rem] text-[var(--p-primary-500)]"></i>
                            </button>
                          } @else if (dbucket && dbucket.totalCostUsd > 0) {
                            <span class="proc-value">${{ dbucket.costPerLbUsd | number: '1.4-4' }}</span>
                          } @else {
                            <span class="proc-value proc-value--zero">$0.0000</span>
                          }
                        </div>
                      }
                      <div class="cell cell--num cell--total tabular-nums">${{ totalCostPerLb(descendant) | number: '1.4-4' }}</div>
                    </div>
                  }
                </div>
              }
              <div class="process-strip">
                @for (process of processes; track process) {
                  @let bucket = bucketFor(fabric, process);
                  @let isRaw = process === 'RawMaterial';
                  <div class="process-strip__cell" [class.process-strip__cell--raw]="isRaw">
                    <span class="process-strip__label">{{ processLabels[process] }}</span>
                    @if (bucket && bucket.totalCostUsd > 0 && !isRaw) {
                      <button
                        type="button"
                        class="process-strip__value group inline-flex items-center gap-1 cursor-pointer"
                        (click)="onProcessClick(fabric, process); $event.stopPropagation()"
                        [attr.aria-label]="processLabels[process] + ' detail'"
                      >
                        <span>${{ bucket.costPerLbUsd | number: '1.4-4' }}</span>
                        <i class="pi pi-arrow-circle-right text-[0.75rem] text-[var(--p-primary-500)]"></i>
                      </button>
                    } @else if (bucket && bucket.totalCostUsd > 0) {
                      <span class="process-strip__value">${{ bucket.costPerLbUsd | number: '1.4-4' }}</span>
                    } @else {
                      <span class="process-strip__value process-strip__value--zero">$0.0000</span>
                    }
                  </div>
                }
              </div>
            </article>
          }
        </div>
        <p-paginator
          [first]="(pageNumber() - 1) * pageSize()"
          [rows]="pageSize()"
          [totalRecords]="r.totalCount"
          [rowsPerPageOptions]="rowsPerPageOptions"
          (onPageChange)="onPaginate($event)"
          styleClass="mt-2 border"
        />
      </div>
    }
  } @else if (!loading()) {
    <div class="empty-state">
      {{ 'Cost_Analysis_No_Data' | translateWF: 'No data for the selected period.' }}
    </div>
  }
</div>
